import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import TopBar from "@/components/TopBar";
import LevelUpModal from "@/components/LevelUpModal";
import { BRANCH_META } from "@/lib/branches";
import { motion } from "framer-motion";
import { Trophy, Coins, Users } from "lucide-react";

export default function Quests() {
    const [quests, setQuests] = useState([]);
    const [reward, setReward] = useState(null);

    useEffect(() => {
        api.get("/quests").then((r) => setQuests(r.data));
    }, []);

    const join = async (quest, branch) => {
        const { data } = await api.post("/quests/join", { quest_slug: quest.slug, branch });
        setReward(data.reward);
    };

    return (
        <div className="min-h-screen pt-24 pb-16 px-4 sm:px-8">
            <TopBar />
            <div className="max-w-6xl mx-auto">
                <div className="flex items-end justify-between">
                    <div>
                        <span
                            className="chip"
                            style={{ background: BRANCH_META.humanities.soft, color: BRANCH_META.humanities.text }}
                        >
                            <Trophy className="w-3 h-3" /> Global Quest Board
                        </span>
                        <h1
                            className="font-display font-black text-5xl sm:text-6xl tracking-tighter text-slate-900 mt-2 leading-none"
                            data-testid="quests-title"
                        >
                            Real problems.
                            <br />
                            <span className="text-humanities">Open teams.</span>
                        </h1>
                        <p className="text-slate-600 mt-3 max-w-xl">
                            External organizations post real-world missions. Every quest breaks itself
                            into four color-coded paths so any type of thinker can jump in.
                        </p>
                    </div>
                </div>

                <div className="mt-8 grid md:grid-cols-2 gap-5">
                    {quests.map((q, qi) => (
                        <motion.div
                            key={q.slug}
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: qi * 0.05 }}
                            className="crystal rounded-3xl p-6"
                            data-testid={`quest-${q.slug}`}
                        >
                            <div className="text-xs uppercase tracking-widest font-bold text-slate-400">
                                Posted by {q.org}
                            </div>
                            <h2 className="font-display font-black text-2xl tracking-tighter text-slate-900 mt-1">
                                {q.title}
                            </h2>
                            <p className="text-slate-600 mt-2">{q.prompt}</p>

                            <div className="mt-4 grid grid-cols-2 gap-2">
                                {q.paths.map((p) => {
                                    const meta = BRANCH_META[p.branch];
                                    return (
                                        <button
                                            key={p.branch}
                                            onClick={() => join(q, p.branch)}
                                            className="text-left rounded-2xl p-3 hover:scale-[1.02] transition-transform"
                                            style={{
                                                background: meta.soft,
                                                border: `1.5px solid ${meta.color}`,
                                            }}
                                            data-testid={`quest-${q.slug}-path-${p.branch}`}
                                        >
                                            <div
                                                className="text-[10px] uppercase tracking-widest font-bold"
                                                style={{ color: meta.text }}
                                            >
                                                {p.label}
                                            </div>
                                            <div className="text-sm text-slate-800 mt-1">
                                                {p.task}
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>

                            <div className="mt-5 flex items-center justify-between text-sm">
                                <div className="flex items-center gap-1 text-slate-600">
                                    <Coins className="w-4 h-4" color={BRANCH_META.arts.color} />{" "}
                                    <span className="font-mono font-bold">
                                        ${q.reward_pool_usd.toLocaleString()}
                                    </span>
                                </div>
                                <div className="chip" style={{ background: BRANCH_META.arts.soft, color: BRANCH_META.arts.text }}>
                                    <Trophy className="w-3 h-3" /> {q.reward_badge}
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>

            <LevelUpModal reward={reward} onClose={() => setReward(null)} />
        </div>
    );
}
